import React, { useState, useEffect, useRef, forwardRef } from "react";
import apiInstance from "../../apiInstance";
import Button from "../../Button";
import PopupModal from "./PopupModal";

const AddPaymentPopup = forwardRef(
  ({ closePopup, addPayment, type, initialData }, ref) => {
    // For popup windows
    const popupModalRef = useRef(null);
    const isDefaultType = type !== "default";
    useEffect(() => {
      if (ref) {
        popupModalRef.current = ref.current;
      }
    }, [ref]);

    // Form data
    const [paymentData, setPaymentData] = useState({
      userId: "",
      amount: "",
      widgetId:  "",
      paymentMethod: "",
      created : "",
    });

    // Options for dropdowns
    const [availableUsers, setAvailableUsers] = useState([]);
    const [userWidgets, setUserWidgets] = useState([]);

    // Form errors
    const [fieldErrors, setFieldErrors] = useState({});

    // Get users when component loads
    useEffect(() => {
      const fetchUsers = async () => {
        try {
          const usersResponse = await apiInstance.get("/devadmin/details");
          setAvailableUsers(usersResponse.data.user || []);
          setPaymentData(prev => ({
            ...prev,
            userId: initialData?.userId?._id || initialData?.userId || "",
            widgetId: initialData?._id || initialData?.id || "",
          }));
        } catch (error) {
          console.error("Error fetching users:", error);
        }
      };

      fetchUsers();
    }, []);

    // Update widgets when user changes
    useEffect(() => {
      if (!paymentData.userId) {
        setUserWidgets([]);
        return;
      }
      const getUserWidgets = (userId) => {
        try {
          const response = availableUsers.find((user) => user._id == userId || user.id == userId);
          if(!response){
            return []
          }
          return response.widgets;
        } catch (error) {
          console.error("Error getting user widgets:", error);
          return [];
        }
      };

      // Load widgets for selected user
      const widgets = getUserWidgets(paymentData.userId);
      setUserWidgets(widgets);
    }, [paymentData.userId]);

    // Handle form changes
    const handleInputChange = (e) => {
      const { name, value } = e.target;
      setPaymentData((prev) => ({
        ...prev,
        [name]: value,
      }));
    };

    // Check form is valid
    const validateForm = () => {
      const errors = {};
      if (!paymentData.userId) {
        errors.userId = "Customer is required.";
      }
      if (
        !paymentData.amount ||
        isNaN(paymentData.amount) ||
        paymentData.amount <= 0
      ) {
        errors.amount = "Valid amount is required.";
      }
      if (!paymentData.widgetId) {
        errors.widgetId = "Widget is required.";
      }
      if (
        !paymentData.paymentMethod ||
        paymentData.paymentMethod.trim() === ""
      ) {
        errors.paymentMethod = "Payment method is required.";
      }
      if (!paymentData.created) {
        errors.paymentMethod = "Created  Date is required.";
      }
      return errors;
    };
    function convertDateToUnix(dateInput) {
      const date = new Date(dateInput);
      return Math.floor(date.getTime() / 1000);
    }
    // Save payment to system
    const createPayment = async () => {
      setFieldErrors({});

      // Check form is valid
      const errors = validateForm();
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        return;
      }

      try {
        // Send to server
        const response = await apiInstance.post(
          "devadmin/payment/" + paymentData.userId,
          {
            userId: paymentData.userId,
            amount: parseFloat(paymentData.amount),
            widgetId: paymentData.widgetId,
            method: paymentData.paymentMethod,
            created : convertDateToUnix(paymentData.created)
          }
        );
        setPaymentData({
          userId: "",
          amount: "",
          widgetId: "",
          paymentMethod: "",
          created : "",
        });
        // Update list and close
        addPayment(response.data.payment);
        closePopup();
      } catch (error) {
        // Show errors
        if (error.response?.status === 400) {
          setFieldErrors(error.response.data.fieldErrors || {});
        } else {
          console.error("Payment creation failed:", error);
        }
      }
    };

    // Add a new user to the list
    const addUser = (user) => {
      setAvailableUsers((prevUsers) => [...prevUsers, user]);
    };

    // Add a widget to the current user
    const addUserOrder = (widget) => {
      if (paymentData.userId) {
        // Update user's widgets
        setAvailableUsers((prevUsers) => {
          return prevUsers.map((user) => {
            if (user._id === paymentData.userId) {
              return {
                ...user,
                widgets: [...(user.widgets || []), widget],
              };
            }
            return user;
          });
        });

        // Add to dropdown
        setUserWidgets((prev) => [...prev, widget]);
      }
    };

    return (
      <div className="mjwidev-manual-order-pp mjwidev-pp">
        <h2>Add Payment</h2>

        <form className="mjwi-row">
          <div className="mjwi-col100">
            {/* Customer Selection */}
           {isDefaultType && <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                whiteSpace: "nowrap",
              }}
            >
              <div className="mjwidev-input-field custom-select-wrapper">
                <label>Customer:</label>
                <select
                  name="userId"
                  value={paymentData.userId}
                  onChange={handleInputChange}
                  required
                  // disabled={!isDefaultType}
                >
                  <option value="">Select Customer</option>
                  {availableUsers.map((user) => (
                    <option
                      key={user._id || user.id}
                      value={user._id ? user._id : user.id}
                    >
                      {user.email}
                    </option>
                  ))}
                </select>
                <div className="custom-arrow">
                  <svg
                    fill="#ffffff"
                    height="16px"
                    width="16px"
                    viewBox="0 0 330 330"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M325.607,79.393c-5.857-5.857-15.355-5.858-21.213,0.001l-139.39,139.393L25.607,79.393 
                c-5.857-5.857-15.355-5.858-21.213,0.001c-5.858,5.858-5.858,15.355,0,21.213l150.004,150c2.813,2.813,6.628,4.393,10.606,4.393 
                s7.794-1.581,10.606-4.394l149.996-150C331.465,94.749,331.465,85.251,325.607,79.393z"
                    ></path>
                  </svg>
                </div>
                {fieldErrors.userId && (
                  <span className="mjwi-error">{fieldErrors.userId}</span>
                )}
              </div>
              <Button
                name="Add User"
                eventHandler={() => popupModalRef.current.openPopup("addUser")}
                className="mjwi-btn-secondary Cancel"
              />
            </div>}
            {/* Amount Field */}
            <div className="mjwidev-input-field">
              <label>Amount:</label>
              <input
                type="number"
                placeholder="$0.00"
                name="amount"
                value={paymentData.amount}
                onChange={handleInputChange}
                min="0.01"
                step="0.01"
                required
              />
              {fieldErrors.amount && (
                <span className="mjwi-error">{fieldErrors.amount}</span>
              )}
            </div>

            {/* Widget Selection */}
            {isDefaultType && <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                whiteSpace: "nowrap",
              }}
            >
              <div className="mjwidev-input-field custom-select-wrapper">
                <label>Widget:</label>
                <select
                  name="widgetId"
                  value={paymentData.widgetId}
                  onChange={handleInputChange}
                  required
                  // disabled={!isDefaultType}
                >
                  <option value="">Select Widget</option>
                  {userWidgets.map(
                    (widget) =>
                      widget.title && (
                        <option
                          key={widget._id || widget.id}
                          value={widget._id ? widget._id : widget.id}
                        >
                          {widget.title}
                        </option>
                      )
                  )}
                </select>
                <div className="custom-arrow">
                  <svg
                    fill="#ffffff"
                    height="16px"
                    width="16px"
                    viewBox="0 0 330 330"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M325.607,79.393c-5.857-5.857-15.355-5.858-21.213,0.001l-139.39,139.393L25.607,79.393 
                c-5.857-5.857-15.355-5.858-21.213,0.001c-5.858,5.858-5.858,15.355,0,21.213l150.004,150c2.813,2.813,6.628,4.393,10.606,4.393 
                s7.794-1.581,10.606-4.394l149.996-150C331.465,94.749,331.465,85.251,325.607,79.393z"
                    ></path>
                  </svg>
                </div>
                {fieldErrors.widgetId && (
                  <span className="mjwi-error">{fieldErrors.widgetId}</span>
                )}
              </div>
              <Button
                name="Add Widget"
                eventHandler={() => popupModalRef.current.openPopup("addOrder")}
                className="mjwi-btn-secondary Cancel"
                disabled={!paymentData.userId}
              />
            </div>}

            {/* Payment Method Field */}
            <div className="mjwidev-input-field">
              <label>Payment Method:</label>
              <input
                type="text"
                name="paymentMethod"
                placeholder="visa, ****4242"
                value={paymentData.paymentMethod}
                onChange={handleInputChange}
                required
              />
              {fieldErrors.paymentMethod && (
                <span className="mjwi-error">{fieldErrors.paymentMethod}</span>
              )}
            </div>
             {/* Created Date Field */}
            <div className="mjwidev-input-field">
              <label>Created Date:</label>
              <input
                type="date"
                name="created"
                value={paymentData.created}
                onChange={handleInputChange}
              />
              {fieldErrors.created && (
                <span className="mjwi-error">{fieldErrors.created}</span>
              )}
            </div>
            </div>
        </form>

        {/* Action Buttons */}
        <div className="mjwidev-bottom-row">
          <Button name="Add Payment" eventHandler={createPayment} />
          <Button name="Cancel" eventHandler={closePopup} />
        </div>

        {/* Popup for users and orders */}
        <PopupModal
          ref={popupModalRef}
          addUser={addUser}
          addOrder={addUserOrder}
          selectedUserId={paymentData.userId}
        />
      </div>
    );
  }
);

export default AddPaymentPopup;
